import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAuth } from "context/AuthContextProvider";
import { ErrorResponse } from "react-router-dom";
import { translate, translateWithVars } from "shared/languages";
import { RegisterUserForm, User } from "shared/types";
import { EMPLOYEE_ROLES, UserType } from "shared/types/enum";
import { invalidateEmployeeLists as invalidateAllEmployeeLists } from "hooks/mutations/employeeLists";
import { invalidateCustomerList } from "hooks/mutations/customerList";
import { removalCountLabels } from "hooks/queries/user";
import { getApiErrorMessage, getApiSuccessMessage, translateApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";

export const createUser = (registerForm: RegisterUserForm): Promise<User> => {
  return client.post(`/v1/user`, registerForm).then((response) => response.data.data);
};

// The staff lists are keyed by the type they were fetched with (see
// getAllStaffByBusinessId): "instructor" for the schedule pickers, "staff",
// and KARYAWAN_STAFF_TYPE for the Karyawan page. A person can hold both
// employee roles and appears on more than one of them, so an employee write
// refreshes all three; the plain `[user_type]` invalidation alone left the
// Karyawan page stale for its five-minute staleTime.
const invalidateEmployeeLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  user_type: string | undefined
) => {
  if (!user_type || !EMPLOYEE_ROLES.includes(user_type as UserType)) return;
  invalidateAllEmployeeLists(queryClient);
};

const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (registerForm: RegisterUserForm) => {
      return client.post(`/v1/user`, registerForm);
    },
    onSuccess: (_, registerForm) => {
      const user_type = registerForm.user_type;
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [user_type] });
      invalidateEmployeeLists(queryClient, user_type);
      // A new employee is a new payee.
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
      getApiSuccessMessage(`${translate(`user_type.${user_type}`)} ${translate("messages.success.create")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

const useUpdateUser = () => {
  const { state, signIn } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ updateForm, userId }: { updateForm: RegisterUserForm; userId: string }) => {
      return client.put(`/v1/user/${userId}`, updateForm);
    },
    onSuccess: ({ data: response }, { updateForm }) => {
      const user = response.data as User;
      const isCurrentUser = state.user?.id === user.id;

      queryClient.invalidateQueries({ queryKey: ["user", user.id] });
      // Also invalidate the list this account shows up in (Klien/Instruktur
      // tab, keyed [user_type, ...] — see getAllCustomerByBusinessId/
      // getAllStaffByBusinessId, both with a 5-minute staleTime) — mirrors
      // useCreateUser's same invalidation. Without this, a name edit only
      // ever refreshes the single-user detail query; the list table (and
      // anywhere else that query is cached) keeps showing the old name
      // until that 5-minute window naturally expires or the page is
      // hard-refreshed.
      if (updateForm.user_type) {
        queryClient.invalidateQueries({ queryKey: [updateForm.user_type] });
        invalidateEmployeeLists(queryClient, updateForm.user_type);
      }
      // A calendar colour feeds the instructor list every colour is read
      // from, also when the profile was opened as an admin's.
      if (updateForm.calendar_color) {
        queryClient.invalidateQueries({ queryKey: [UserType.Instructor] });
      }

      if (isCurrentUser) {
        // Only update the user state if its the current user edited
        signIn(state.jwtToken, state.refreshToken, user, state.business);
        getApiSuccessMessage(translate("messages.success.update_my_profile"));
      } else {
        getApiSuccessMessage(`${user.full_name} ${translate("messages.success.update")}`);
      }
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      translateApiErrorMessage(error);
    },
  });
};

// A client's (de)activation flips what the package page, the booking and
// waitlist rows and the Reward tab let the admin change
// (util/client-status.ts on the server), and those read their lock from
// their own queries.
const invalidateClientLockQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  userType: string
) => {
  if (userType !== UserType.Customer) return;
  for (const key of ["customer_package", "booking-v2", "waitlist-v2", "loyalty"]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
};

// After a deactivation or role removal that deleted upcoming classes: the
// calendar, schedule detail, waitlists and the clients' packages lose the
// removed classes and get their credits back.
const invalidateAfterClassRemoval = (queryClient: ReturnType<typeof useQueryClient>) => {
  for (const key of [
    "schedule-v2",
    "schedule",
    "waitlist-v2",
    "booking-v2",
    "customer_package",
    "notification",
  ]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
  // Refunded credits change the Klien list's package count.
  invalidateCustomerList(queryClient);
};

const useDeactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      params,
    }: {
      userId: string;
      // remove_upcoming_classes (staff only): delete every class of theirs
      // that hasn't started, in the same step (Hapus Jadwal Mendatang &
      // Nonaktifkan).
      params: { user_type: string; remove_upcoming_classes?: boolean };
    }) => {
      return client.put(`/v1/user/deactivate/${userId}`, params);
    },
    onSuccess: ({ data: response }, { params }) => {
      const removal: { removed_classes: number; refunded_bookings: number } | null =
        response.data?.removal ?? null;
      queryClient.invalidateQueries({ queryKey: ["user", response.data.user_id] });
      // Refresh the business admin list so its active/inactive badge updates.
      queryClient.invalidateQueries({ queryKey: ["business"] });
      // Also invalidate the list this account shows up in (Klien/Instruktur
      // tab, keyed [user_type, ...] — see getAllCustomerByBusinessId/
      // getAllStaffByBusinessId) — same reasoning as useUpdateUser's own
      // invalidation. Without this, the Status badge on the list page kept
      // showing the pre-deactivation value until the 5-minute staleTime
      // naturally expired or the page was hard-refreshed.
      queryClient.invalidateQueries({ queryKey: [params.user_type] });
      invalidateEmployeeLists(queryClient, params.user_type);
      // Payroll's staff roster lists active employees only.
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
      invalidateClientLockQueries(queryClient, params.user_type);
      if (removal) {
        invalidateAfterClassRemoval(queryClient);
        getApiSuccessMessage(
          translateWithVars(
            "staff_status.removed_toast",
            removalCountLabels(removal.removed_classes, removal.refunded_bookings)
          )
        );
        return;
      }
      getApiSuccessMessage(`User ${translate("messages.success.deactivate")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

const useReactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, params }: { userId: string; params: { user_type: string } }) => {
      return client.put(`/v1/user/reactivate/${userId}`, params);
    },
    onSuccess: ({ data: response }, { params }) => {
      queryClient.invalidateQueries({ queryKey: ["user", response.data.user_id] });
      queryClient.invalidateQueries({ queryKey: ["business"] });
      // See useDeactivateUser's matching invalidation for why this is needed.
      queryClient.invalidateQueries({ queryKey: [params.user_type] });
      invalidateEmployeeLists(queryClient, params.user_type);
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
      invalidateClientLockQueries(queryClient, params.user_type);
      getApiSuccessMessage(`User ${translate("messages.success.reactivate")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
export type StaffAdminTier = "none" | "admin" | "manager";
export interface SetStaffRolesBody {
  instructor: boolean;
  admin_tier: StaffAdminTier;
  // Only read by the server on an admin-tier grant to an account with no
  // password yet.
  password?: string;
  // Instructor revoke only: delete every upcoming class of theirs too.
  remove_upcoming_classes?: boolean;
}

// System admin only (PUT /v1/admin/staff/:id/roles): the full desired role
// state for one staff member. Invalidates every list the person can appear
// on: the detail view, Bisnis -> Akun, the Instruktur list and pickers, and
// payroll's staff roster; plus the login-code read, since an instructor grant
// mints the code and link the profile card shows.
const useSetStaffRoles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, body }: { userId: string; body: SetStaffRolesBody }) => {
      return client.put(`/v1/admin/staff/${userId}/roles`, body);
    },
    onSuccess: ({ data: response }, { userId }) => {
      const removal: { removed_classes: number; refunded_bookings: number } | null =
        response.data?.removal ?? null;
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["business"] });
      // A role change moves the person between the Instruktur / Staf lists.
      invalidateAllEmployeeLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
      queryClient.invalidateQueries({ queryKey: ["staff", userId, "login-code"] });
      if (removal) {
        invalidateAfterClassRemoval(queryClient);
        getApiSuccessMessage(
          translateWithVars(
            "staff_status.role.removed_toast",
            removalCountLabels(removal.removed_classes, removal.refunded_bookings)
          )
        );
        return;
      }
      getApiSuccessMessage(translate("staff_roles.success"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// The Manager's instructor on/off switch for one of their studio's admin-tier
// accounts (PUT /v1/staff/:id/instructor-role). Same invalidations as the
// full editor: the person can appear on every one of those lists.
// The calendar colour on its own (PUT /v1/staff/:id/calendar-color), for a
// profile whose form is read-only to the viewer: an admin or Manager who also
// teaches. Everyone else's colour saves with their profile (useUpdateUser).
const useSetStaffCalendarColor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      calendarColor,
    }: {
      userId: string;
      calendarColor: string;
      // For the toast only.
      fullName: string;
    }) => {
      return client.put(`/v1/staff/${userId}/calendar-color`, {
        calendar_color: calendarColor,
      });
    },
    onSuccess: (_, { userId, fullName }) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      // Every block's colour is read from the instructor list.
      queryClient.invalidateQueries({ queryKey: [UserType.Instructor] });
      getApiSuccessMessage(`${fullName} ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      translateApiErrorMessage(error);
    },
  });
};

const useSetStaffInstructorRole = () => {
  const { state, signIn } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      instructor,
      removeUpcoming,
    }: {
      userId: string;
      instructor: boolean;
      // Revoke only: delete every upcoming class of theirs in the same step.
      removeUpcoming?: boolean;
    }) => {
      return client.put(`/v1/staff/${userId}/instructor-role`, {
        instructor,
        ...(removeUpcoming ? { remove_upcoming_classes: true } : {}),
      });
    },
    onSuccess: ({ data: response }, { userId }) => {
      const removal: { removed_classes: number; refunded_bookings: number } | null =
        response.data?.removal ?? null;
      // A Manager toggling their OWN hat: the stored session user carries the
      // roles the Peran badges read, so refresh it the way useUpdateUser does.
      if (state.user?.id === userId) {
        signIn(state.jwtToken, state.refreshToken, response.data as User, state.business);
      }
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["business"] });
      // A role change moves the person between the Instruktur / Staf lists.
      invalidateAllEmployeeLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ["payroll", "staff"] });
      queryClient.invalidateQueries({ queryKey: ["staff", userId, "login-code"] });
      if (removal) {
        invalidateAfterClassRemoval(queryClient);
        getApiSuccessMessage(
          translateWithVars(
            "staff_status.role.removed_toast",
            removalCountLabels(removal.removed_classes, removal.refunded_bookings)
          )
        );
        return;
      }
      getApiSuccessMessage(translate("staff_roles.success"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export {
  useCreateUser,
  useUpdateUser,
  useDeactivateUser,
  useReactivateUser,
  useSetStaffRoles,
  useSetStaffInstructorRole,
  useSetStaffCalendarColor,
};
